import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { clamp, xFrame, zoomWindow, type TimeWindow } from './timeline-scale';
import type { useTimelineViewport } from './use-timeline-viewport';

export interface TimelineDrag { kind: string; id: string; origin: number; frame: number; moved: boolean }
export function useTimelineGesture(options: {
  viewport: ReturnType<typeof useTimelineViewport>; last: number; frame: number;
  onPause: () => void; onSeek: (frame: number) => void;
  onCommit: (drag: TimelineDrag) => void; onSelect: (id: string) => void;
}) {
  const latest = useRef(options); latest.current = options;
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const active = useRef<(TimelineDrag & { x: number; y: number; start: TimeWindow; playhead: number }) | null>(null);
  const pinch = useRef<{ distance: number; anchor: number; view: TimeWindow } | null>(null);
  const [drag, setDrag] = useState<TimelineDrag | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const coordinate = (clientX: number) => {
    const { viewport } = latest.current;
    return clientX - (viewport.host.current?.getBoundingClientRect().left ?? 0) - 16;
  };
  const position = (clientX: number) => {
    const { viewport, last } = latest.current;
    return xFrame(coordinate(clientX), viewport.current.current, viewport.width, last);
  };
  const cancel = () => {
    if (active.current?.kind === 'seek') latest.current.onSeek(active.current.playhead);
    active.current = null; setDrag(null);
  };
  useEffect(() => {
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape' && active.current) { event.preventDefault(); event.stopPropagation(); cancel(); } };
    document.addEventListener('keydown', escape, true);
    return () => document.removeEventListener('keydown', escape, true);
  }, []);
  useEffect(() => {
    const host = options.viewport.host.current; if (!host) return;
    const wheel = (event: WheelEvent) => {
      const { viewport } = latest.current;
      if (event.ctrlKey) {
        event.preventDefault(); const fraction = clamp(coordinate(event.clientX) / viewport.width, 0, 1);
        viewport.zoom(Math.exp(-event.deltaY * .01), viewport.current.current.start + fraction * viewport.current.current.span, fraction);
      } else if (Math.abs(event.deltaX) > 0 || event.shiftKey) {
        event.preventDefault(); viewport.pan(viewport.current.current.start + (event.deltaX || event.deltaY) / viewport.width * viewport.current.current.span);
      }
    };
    let scale = 1;
    const safariStart = (event: Event) => { event.preventDefault(); cancel(); scale = 1; };
    const safariChange = (event: Event) => {
      event.preventDefault(); const gesture = event as Event & { scale: number; clientX: number };
      const { viewport } = latest.current; const fraction = clamp(coordinate(gesture.clientX) / viewport.width, 0, 1);
      viewport.zoom(gesture.scale / scale, viewport.current.current.start + fraction * viewport.current.current.span, fraction); scale = gesture.scale;
    };
    host.addEventListener('wheel', wheel, { passive: false });
    host.addEventListener('gesturestart', safariStart, { passive: false }); host.addEventListener('gesturechange', safariChange, { passive: false });
    return () => { host.removeEventListener('wheel', wheel); host.removeEventListener('gesturestart', safariStart); host.removeEventListener('gesturechange', safariChange); };
  }, []);
  const down = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointers.current.size === 2) {
      cancel(); const [a, b] = [...pointers.current.values()]; const { viewport } = latest.current;
      pinch.current = { distance: Math.max(1, Math.hypot(a!.x - b!.x, a!.y - b!.y)), view: viewport.current.current,
        anchor: viewport.current.current.start + clamp(coordinate((a!.x + b!.x) / 2) / viewport.width, 0, 1) * viewport.current.current.span };
      event.currentTarget.setPointerCapture(event.pointerId); event.preventDefault(); return;
    }
    if (pinch.current || pointers.current.size > 1) return;
    const button = (event.target as HTMLElement).closest('button');
    if (button && !button.hasAttribute('data-timeline-action')) return;
    const target = (event.target as HTMLElement).closest<HTMLElement>('[data-timeline-action]');
    if (!target) return;
    const kind = target.dataset.timelineAction!; const origin = Number(target.dataset.frame ?? latest.current.frame);
    active.current = { kind, id: target.dataset.id ?? '', origin, frame: origin, moved: false, x: event.clientX, y: event.clientY,
      start: latest.current.viewport.current.current, playhead: latest.current.frame };
    if (kind !== 'pan') latest.current.onPause();
    if (kind === 'seek') { active.current.frame = position(event.clientX); latest.current.onSeek(active.current.frame); }
    setDrag(active.current); event.currentTarget.setPointerCapture(event.pointerId); event.preventDefault();
  };
  const move = (event: ReactPointerEvent<HTMLDivElement>) => {
    setHover(position(event.clientX));
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const { viewport, last } = latest.current;
    if (pinch.current && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()]; const value = pinch.current;
      const fraction = clamp(coordinate((a!.x + b!.x) / 2) / viewport.width, 0, 1);
      viewport.set(zoomWindow(value.view, Math.hypot(a!.x - b!.x, a!.y - b!.y) / value.distance, value.anchor, fraction, last));
      return;
    }
    const value = active.current; if (!value) return;
    if (event.pointerType === 'touch' && !value.moved && Math.abs(event.clientY - value.y) > Math.max(6, Math.abs(event.clientX - value.x))) {
      if (value.kind === 'pan') viewport.set(value.start);
      cancel(); return;
    }
    const delta = (event.clientX - value.x) / viewport.width * value.start.span;
    value.moved ||= Math.abs(event.clientX - value.x) > 3;
    if (value.kind === 'pan') viewport.pan(value.start.start - delta);
    else {
      value.frame = value.kind === 'seek' ? position(event.clientX) : Math.round(clamp(value.origin + delta, 0, last));
      if (value.kind === 'seek') latest.current.onSeek(value.frame);
    }
    setDrag({ ...value });
  };
  const up = (event: ReactPointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    if (pinch.current) { if (!pointers.current.size) pinch.current = null; return; }
    const value = active.current; active.current = null; setDrag(null);
    if (!value) return;
    if (value.kind === 'waypoint' && !value.moved) latest.current.onSelect(value.id);
    else if (value.kind === 'pan' && !value.moved) latest.current.onSeek(position(event.clientX));
    else if (value.kind !== 'pan') latest.current.onCommit(value);
  };
  return { drag, hover, handlers: {
    onPointerDownCapture: down, onPointerMove: move, onPointerUp: up,
    onPointerCancel: (event: ReactPointerEvent<HTMLDivElement>) => { pointers.current.delete(event.pointerId); cancel(); if (!pointers.current.size) pinch.current = null; },
    onLostPointerCapture: (event: ReactPointerEvent<HTMLDivElement>) => { if (pointers.current.has(event.pointerId)) { pointers.current.delete(event.pointerId); cancel(); } },
    onPointerLeave: () => setHover(null),
  } };
}
